/**
 * Ticket recipient-page presentation — Sprint 9 WI-5.
 *
 * Pure helpers derived from server preview fields + `effectiveTicketStatus`.
 * No lifecycle authority; display only.
 */
import { TicketStatus, effectiveTicketStatus } from './ticketStatus';

function toMillis(val) {
  if (val == null) return null;
  if (typeof val === 'number') return val;
  if (val?.toMillis) return val.toMillis();
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Human-readable time remaining until a ticket expires. Returns null when
 * unknown or already terminal.
 *
 * @param {object|null|undefined} preview
 * @param {{ now?: number|Date }} [opts]
 * @returns {string|null}
 */
export function formatTicketExpiryRemaining(preview, { now } = {}) {
  const status = effectiveTicketStatus(preview, { now });
  if (status !== TicketStatus.OPEN) return null;
  const expiresMs = toMillis(preview?.expiresAt);
  if (expiresMs == null) return null;
  const nowMs = now == null ? Date.now() : toMillis(now);
  const diff = expiresMs - nowMs;
  if (diff <= 0) return 'Expired';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m left`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return remMins > 0 ? `${hours}h ${remMins}m left` : `${hours}h left`;
  const days = Math.floor(hours / 24);
  return `${days}d left`;
}

/** Subtitle for open-ticket states: who sent the invite. */
export function ticketSenderLine(preview) {
  const name = String(preview?.creatorDisplayName || '').trim();
  return name ? `${name} sent you a challenge` : null;
}

/** Matchup title from preview teams. */
export function ticketMatchupLabel(preview) {
  if (preview?.teamA && preview?.teamB) return `${preview.teamA} vs ${preview.teamB}`;
  return 'Head-to-head challenge';
}

/**
 * Whether the preview has enough context for a terminal recap card.
 * @param {object|null|undefined} preview
 * @returns {boolean}
 */
export function hasTicketTerminalRecap(preview) {
  return Boolean(preview?.teamA && preview?.teamB);
}
